import { Ohje } from "./Ohje";
import { t, virheTeksti } from "./kieli";
import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { useTaulukko } from "./taulukko";
import { nayttonimi, vaihtoehdot } from "./nayttonimi";
import { api, ApiError, type Kayttaja, type Asiakas, type Asiakasrooli } from "./api";

/* ═══════════════════════════════════════════════════════════
   Asiakasrekisteri.

   Asiakas on oma rekisterinsä, ei per-tarjous kirjattuna. Tarjouksen
   teko voi luoda asiakkaan lennossa; täällä tiedot täydennetään ja
   pidetään ajan tasalla. Asiakkaan yhteyshenkilö = tarjouksen tilaaja.
   ═══════════════════════════════════════════════════════════ */

const TYHJA = { nimi: "", y_tunnus: "", osoite: "", yhteyshenkilo: "", puhelin: "", sahkoposti: "" };

export default function Asiakkaat({ kayttaja, osoiteHaku, asetaOsoiteHaku }: { kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (h: string) => void }) {
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));
  const [lista, setLista] = useState<Asiakas[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [avattu, setAvattu] = useState<number | null>(null);
  const [uusiAuki, setUusiAuki] = useState(false);
  const [uusi, setUusi] = useState({ ...TYHJA });
  const [haetaan, setHaetaan] = useState(false);
  /* 150: roolinimet kannasta — joukko on avoin, joten vakiolista
     selaimessa olisi toinen totuus. Tyhjä lista on kelvollinen tila
     (kohde_asiakas voi olla käyttämättä), ja silloin sarake on viiva. */
  const [roolit, setRoolit] = useState<Asiakasrooli[]>([]);

  const haeUusiYtj = async () => {
    const yt = uusi.y_tunnus.trim();
    if (!/^\d{7}-\d$/.test(yt)) { setVirhe(t("asi.anna_ytunnus")); return; }
    setHaetaan(true); setVirhe(null);
    try {
      const r = await api.haeYtj(yt);
      setUusi((u) => ({ ...u, nimi: r.nimi || u.nimi, y_tunnus: r.y_tunnus || u.y_tunnus, osoite: r.osoite_koko || u.osoite }));
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("asi.ytj_virhe")); }
    finally { setHaetaan(false); }
  };

  // A3–A7: jaettu lajittelu ja haku (taulukko.tsx).
  const [naytaPoistetut, setNaytaPoistetut] = useState(false);
  const tk = useTaulukko(lista, {
    avaimet: { nimi: (x) => x.nimi, ytunnus: (x) => x.y_tunnus, yhteyshenkilo: (x) => x.yhteyshenkilo },
    hakukentat: (x) => [x.nimi, x.y_tunnus, x.yhteyshenkilo, x.sahkoposti, x.puhelin],
    oletus: "nimi", yksikko: t("asi.yksikko"), osoiteHaku, asetaOsoiteHaku,
  });
  const lataa = useCallback(async () => {
    try {
      const [as, rl] = await Promise.all([api.asiakkaat(naytaPoistetut), api.asiakasroolit()]);
      setLista(as); setRoolit(rl); setVirhe(null);
    }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pc.lataus_virhe")); setLista([]); }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);

  const luo = async () => {
    if (!uusi.nimi.trim()) { setVirhe(t("asi.anna_nimi")); return; }
    try {
      await api.luoAsiakas({
        nimi: uusi.nimi.trim(),
        y_tunnus: uusi.y_tunnus.trim() || null, osoite: uusi.osoite.trim() || null,
        yhteyshenkilo: uusi.yhteyshenkilo.trim() || null,
        puhelin: uusi.puhelin.trim() || null, sahkoposti: uusi.sahkoposti.trim() || null,
      });
      setUusi({ ...TYHJA }); setUusiAuki(false); setVirhe(null);
      await lataa();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("asi.lisays_virhe")); }
  };

  return (
    <>
      <header className="head">
        <div className="head__rivi">
          <h1>{t("asi.otsikko")}</h1>
          {saaMuokata && (
            <button className="nappi nappi--paa" onClick={() => setUusiAuki((v) => !v)}>
              {uusiAuki ? t("hl.peruuta") : t("asi.uusi")}
            </button>
          )}
        </div>
      </header>

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/asiakkaat*: vaadiRooli(paakayttaja, suunnittelija) + moduuli tarjous; poisto on merkintä (118). */}
      <Ohje
        kuka={<>{t("asi.ohje.kuka")}</>}
        luvut={<>{t("asi.ohje.luvut")}</>} />
      <section className="panel">
        {virhe && <p className="huom" role="alert">{virhe}</p>}

        {uusiAuki && saaMuokata && (
          <div className="asiakas__lomake">
            {([["nimi",t("asi.yritys")],["y_tunnus",t("asi.y_tunnus")],["osoite",t("asi.osoite")],
               ["yhteyshenkilo",t("asi.yhteyshenkilo_tilaaja")],["puhelin",t("omat.puhelin")],["sahkoposti",t("kirjautuminen.sahkoposti")]] as const)
              .map(([k, l]) => (
                <label key={k}>{l}
                  <input value={(uusi as any)[k]} onChange={(e) => setUusi((u) => ({ ...u, [k]: e.target.value }))} />
                </label>
              ))}
            <button type="button" className="nappi nappi--kevyt" disabled={haetaan} onClick={haeUusiYtj}
              title={t("asi.ytj_title")}>{haetaan ? t("asi.haetaan") : t("asi.hae_ytj")}</button>
            <button className="nappi nappi--paa" onClick={luo}>{t("asi.lisaa")}</button>
          </div>
        )}

        {lista === null && <p className="dim">{t("ltar.ladataan")}</p>}
        {lista && lista.length === 0 && <p className="dim">{t("asi.ei_asiakkaita")}</p>}

        <label className="dim" style={{ display: "block", marginBottom: 8 }}>
          <input type="checkbox" checked={naytaPoistetut} onChange={(e) => setNaytaPoistetut(e.target.checked)} />
          {" "}{t("asi.nayta_poistetut")}
        </label>
        {lista && lista.length > 0 && (
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
            {tk.Haku({ placeholder: t("asi.haku") })}
            {tk.piilotettu > 0 && <span className="dim">{t("asi.piilossa", { n: tk.piilotettu })}</span>}
          </div>
        )}
        {lista && lista.length > 0 && (
          <table className="selailu__taulu asiakas__taulu">
            <thead>
              <tr><th>{tk.Otsikko("nimi", t("asi.yritys"))}</th><th>{tk.Otsikko("ytunnus", t("asi.y_tunnus"))}</th><th>{tk.Otsikko("yhteyshenkilo", t("asi.yhteyshenkilo"))}</th><th>{t("omat.puhelin")}</th><th>{t("kirjautuminen.sahkoposti")}</th><th className="th--vasen">{t("valikko.tyokohteet")}</th><th></th></tr>
            </thead>
            <tbody>
              {tk.rivit.map((a) => (
                <Rivi key={a.id} a={a} auki={avattu === a.id} saaMuokata={saaMuokata}
                  avaa={() => setAvattu(avattu === a.id ? null : a.id)} onMuutos={lataa}
                  roolit={roolit} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function Rivi({ a, auki, saaMuokata, avaa, onMuutos, roolit }: {
  a: Asiakas; auki: boolean; saaMuokata: boolean; avaa: () => void;
  onMuutos: () => Promise<void>; roolit: Asiakasrooli[];
}) {
  const [muok, setMuok] = useState(a);
  const [haetaan, setHaetaan] = useState(false);
  // 118: poisto on tietue — kuka, milloin, syy, peruste. Palautus kysyy oman perusteensa.
  const [poistoAuki, setPoistoAuki] = useState(false);
  const [poistoSyy, setPoistoSyy] = useState("virheellinen");
  const [poistoPeruste, setPoistoPeruste] = useState("");
  const [palautusPeruste, setPalautusPeruste] = useState("");
  const [ilmo, setIlmo] = useState<string | null>(null);
  const poista = async () => {
    if (poistoPeruste.trim().length < 3) { setIlmo(t("asi.peruste_poisto")); return; }
    try { await api.poistaAsiakas(a.id, { syy: poistoSyy, peruste: poistoPeruste.trim() }); setPoistoAuki(false); await onMuutos(); }
    catch (e) { setIlmo(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe")); }
  };
  const palauta = async () => {
    if (palautusPeruste.trim().length < 3) { setIlmo(t("asi.peruste_palautus")); return; }
    try { await api.palautaAsiakas(a.id, { peruste: palautusPeruste.trim() }); await onMuutos(); }
    catch (e) { setIlmo(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("asi.palautus_virhe")); }
  };
  useEffect(() => { setMuok(a); }, [a]);
  const haeYtj = async () => {
    const yt = (muok.y_tunnus ?? "").trim();
    if (!/^\d{7}-\d$/.test(yt)) { window.alert(t("asi.ytunnus_muoto")); return; }
    setHaetaan(true);
    try {
      const r = await api.haeYtj(yt);
      setMuok((m) => ({ ...m, nimi: r.nimi || m.nimi, y_tunnus: r.y_tunnus || m.y_tunnus, osoite: r.osoite_koko || m.osoite }));
    } catch (e) { window.alert(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("asi.ytj_virhe")); }
    finally { setHaetaan(false); }
  };
  const tallenna = async () => {
    await api.paivitaAsiakas(a.id, {
      nimi: muok.nimi.trim(), y_tunnus: muok.y_tunnus?.trim() || null,
      osoite: muok.osoite?.trim() || null, yhteyshenkilo: muok.yhteyshenkilo?.trim() || null,
      puhelin: muok.puhelin?.trim() || null, sahkoposti: muok.sahkoposti?.trim() || null,
    });
    await onMuutos(); avaa();
  };
  return (
    <>
      <tr className={a.poistettu ? "tr--passiivi" : ""}>
        <td>{a.nimi}{a.poistettu && <span className="dim" title={a.poisto_peruste ?? ""}> · {t("asi.poistettu_pvm", { pvm: naytaPvm(a.poistettu) ?? "" })} · {nayttonimi("asiakas_poisto_syy", a.poisto_syy ?? "muu")}</span>}</td>
        <td className="mono">{a.y_tunnus ?? ""}</td>
        <td>{a.yhteyshenkilo ?? ""}</td>
        <td className="mono">{a.puhelin ?? ""}</td>
        <td className="dim">{a.sahkoposti ?? ""}</td>
        {/* 149: kohteet rooleittain eikä yhtenä summana. «3 työkohdetta» ei
            kerro onko asiakas tilaaja vai loppuasiakas, ja se ero on koko
            roolin tarkoitus. Tyhjä on viiva eikä nolla: ei liitoksia ja
            nolla kohdetta näyttäisivät muuten samalta. */}
        <td>
          {Object.keys(a.kohteita_rooleittain ?? {}).length === 0
            ? <span className="dim">—</span>
            : roolit
                .filter((r) => (a.kohteita_rooleittain?.[r.tunnus] ?? 0) > 0)
                .map((r, i) => (
                  <span key={r.tunnus}>
                    {i > 0 && " · "}
                    {a.kohteita_rooleittain![r.tunnus]} {r.nimi.toLowerCase()}
                  </span>
                ))}
        </td>
        <td>{saaMuokata && <button className="nappi nappi--kevyt" onClick={avaa}>{auki ? t("hl.sulje") : t("qr.muokkaa")}</button>}</td>
      </tr>
      {auki && saaMuokata && (
        <tr className="asiakas__muokkausrivi">
          <td colSpan={7}>
            <div className="asiakas__lomake">
              {([["nimi",t("asi.yritys")],["y_tunnus",t("asi.y_tunnus")],["osoite",t("asi.osoite")],
                 ["yhteyshenkilo",t("asi.yhteyshenkilo_tilaaja")],["puhelin",t("omat.puhelin")],["sahkoposti",t("kirjautuminen.sahkoposti")]] as const)
                .map(([k, l]) => (
                  <label key={k}>{l}
                    <input value={(muok as any)[k] ?? ""} onChange={(e) => setMuok((m) => ({ ...m, [k]: e.target.value }))} />
                  </label>
                ))}
              <button type="button" className="nappi nappi--kevyt" disabled={haetaan} onClick={haeYtj}
                title={t("asi.ytj_title")}>{haetaan ? t("asi.haetaan") : t("asi.hae_ytj")}</button>
              <button className="nappi nappi--paa" onClick={tallenna}>{t("ltar.tallenna")}</button>
            </div>
            {a.poistettu ? (
              <p className="huom">
                {t("asi.poistettu_rekisterista", { pvm: naytaPvm(a.poistettu) ?? "", kuka: a.poistaja ?? "" })} <b>{nayttonimi("asiakas_poisto_syy", a.poisto_syy ?? "muu")}</b> — {a.poisto_peruste}
                {a.tarjouksia > 0 && <span className="dim"> · {t("asi.tarjouksia_sailyy", { n: a.tarjouksia })}</span>}
                <span className="allokointi" style={{ marginTop: 8 }}>
                  <label>{t("asi.palautuksen_peruste")}
                    <input type="text" size={30} value={palautusPeruste} onChange={(e) => setPalautusPeruste(e.target.value)} /></label>
                  <button type="button" className="nappi nappi--kevyt" onClick={() => void palauta()}>{t("asi.palauta")}</button>
                </span>
              </p>
            ) : (
              <div className="allokointi" style={{ marginTop: 8 }}>
                {!poistoAuki && <button type="button" className="nappi nappi--kevyt" onClick={() => setPoistoAuki(true)}>{t("asi.poista_avaa")}</button>}
                {poistoAuki && (
                  <>
                    <label>{t("qr.syy")}
                      <select value={poistoSyy} onChange={(e) => setPoistoSyy(e.target.value)}>
                        {vaihtoehdot("asiakas_poisto_syy").map((v) => <option key={v.arvo} value={v.arvo} title={v.selite}>{v.nimi}</option>)}
                      </select></label>
                    <label>{t("asi.peruste_pakollinen")}
                      <input type="text" size={36} value={poistoPeruste} placeholder={t("asi.peruste_esim")}
                             onChange={(e) => setPoistoPeruste(e.target.value)} /></label>
                    <button type="button" className="nappi nappi--poisto" disabled={poistoPeruste.trim().length < 3} onClick={() => void poista()}>{t("asi.poista")}</button>
                    <button type="button" className="nappi nappi--kevyt" onClick={() => setPoistoAuki(false)}>{t("hl.peruuta")}</button>
                    {a.tarjouksia > 0 && <span className="dim">{t("asi.tarjouksia_poistettuna", { n: a.tarjouksia })}</span>}
                  </>
                )}
              </div>
            )}
            {ilmo && <p className="huom" role="status">{ilmo}</p>}
          </td>
        </tr>
      )}
    </>
  );
}
